import { EVENT_SITES } from '@crossbeam/itly';

import {
  MY_POPULATIONS_FILTER,
  PARTNER_POPULATIONS_FILTER,
  type PartnerTabFilterValues,
  type PopulationFiltersType,
} from '@/components/sharing-settings/partner-tab/composables/usePartnerTabFilters';
import type {
  PopulationFilterSelectionType,
  SharingLevelVariantType,
  SharingSideType,
} from '@/components/sharing-settings/partner-tab/types';

import useIteratively from '@/composables/useIteratively';
import type { SegmentType } from '@/types/populations';

type PopulationSideType = keyof PopulationFiltersType;
type SharingSettingsTabType = 'by_partners' | 'by_populations';

const POPULATION_SIDE_BY_SHARING_SIDE: Record<
  SharingSideType,
  PopulationSideType
> = {
  incoming: PARTNER_POPULATIONS_FILTER,
  outgoing: MY_POPULATIONS_FILTER,
};

type PartnerSettingsOpenedType = {
  tab: SharingSettingsTabType;
  partnerOrgId: number;
  segment: SegmentType | null;
} & (
  | {
      entryPoint: 'cell';
      side: SharingSideType;
      sharingLevel: SharingLevelVariantType | null;
      /**
       * The segment's overrides on a cell, the clicked population's from the
       * popover. Above 0, the Overrides filter is requested. Left out on the
       * Partner Populations side, where overrides are not computed.
       */
      overridesCount?: number;
    }
  | {
      entryPoint: 'popover_row' | 'popover_override';
      side: SharingSideType;
      sharingLevel: SharingLevelVariantType;
      overridesCount?: number;
      populationId: number;
    }
  | { entryPoint: 'requests_drawer'; populationId: number }
);

type BulkTagsSavedType = {
  partnerCount: number;
  savedCount: number;
  addedCount: number;
  removedCount: number;
  isSuccess: boolean;
  isTagLimitReached: boolean;
};

/**
 * Usage tracking for Sharing Settings: the page events both tabs share, and
 * the By Partners tab's own, all sent as `data_sharing_action`.
 */
export default function useSharingSettingsTracking() {
  const { iteratively } = useIteratively();

  function track(
    eventSite: string,
    action: string,
    metadata: Record<string, unknown> = {},
  ) {
    iteratively.dataSharingAction({
      event_site: eventSite,
      metadata: { action, ...metadata },
    });
  }

  function trackPartnerTab(
    action: string,
    metadata: Record<string, unknown> = {},
  ) {
    track(EVENT_SITES.DEFAULT_SHARING_PARTNERS_PAGE, action, metadata);
  }

  function trackSearch(searchTerm: string) {
    trackPartnerTab('search', { search_term: searchTerm });
  }

  /** @param activeFilters - Only the filters holding a value */
  function trackFilters(activeFilters: PartnerTabFilterValues) {
    const action = Object.keys(activeFilters).length
      ? 'applied_filters'
      : 'cleared_filters';
    trackPartnerTab(action, { filter_values: activeFilters });
  }

  function trackPopulationFilterApplied(
    side: PopulationSideType,
    { operator, levels, items }: PopulationFilterSelectionType,
  ) {
    trackPartnerTab('applied_population_filter', {
      side,
      operator,
      levels,
      segment_count: new Set(items.map(({ segment }) => segment)).size,
      population_count: items.reduce(
        (count, item) =>
          count +
          ('population_ids' in item ? (item.population_ids?.length ?? 0) : 0),
        0,
      ),
    });
  }

  function trackPopulationFilterCleared(side: PopulationSideType) {
    trackPartnerTab('cleared_population_filter', { side });
  }

  function trackSharingPopoverViewed(
    side: SharingSideType,
    segment: SegmentType,
    sharingLevel: SharingLevelVariantType | null,
  ) {
    trackPartnerTab('viewed_sharing_popover', {
      side: POPULATION_SIDE_BY_SHARING_SIDE[side],
      segment,
      sharing_level: sharingLevel,
    });
  }

  function trackPartnerSettingsOpened(opened: PartnerSettingsOpenedType) {
    const entryMetadata =
      opened.entryPoint === 'requests_drawer'
        ? { population_id: opened.populationId }
        : {
            side: POPULATION_SIDE_BY_SHARING_SIDE[opened.side],
            sharing_level: opened.sharingLevel,
            ...(opened.overridesCount !== undefined && {
              overrides_count: opened.overridesCount,
            }),
            ...(opened.entryPoint !== 'cell' && {
              population_id: opened.populationId,
            }),
          };
    track(EVENT_SITES.SHARING_SETTINGS_PAGE, 'opened_partner_settings', {
      tab: opened.tab,
      entry_point: opened.entryPoint,
      partner_org_id: opened.partnerOrgId,
      segment: opened.segment,
      ...entryMetadata,
    });
  }

  function trackRequestsDrawerOpened(
    tab: SharingSettingsTabType,
    pendingCount: number,
  ) {
    track(EVENT_SITES.SHARING_SETTINGS_PAGE, 'opened_requests_drawer', {
      tab,
      pending_count: pendingCount,
    });
  }

  function trackOwnerChanged(isCleared: boolean) {
    trackPartnerTab('owner_changed', { cleared: isCleared });
  }

  function trackBulkTagsSaved({
    partnerCount,
    savedCount,
    addedCount,
    removedCount,
    isSuccess,
    isTagLimitReached,
  }: BulkTagsSavedType) {
    trackPartnerTab('bulk_tags_saved', {
      partner_count: partnerCount,
      saved_count: savedCount,
      added_count: addedCount,
      removed_count: removedCount,
      success: isSuccess,
      tag_limit_reached: isTagLimitReached,
    });
  }

  function trackBulkRemoveConfirmed(
    partnerCount: number,
    removedCount: number,
  ) {
    trackPartnerTab('bulk_remove_confirmed', {
      partner_count: partnerCount,
      removed_count: removedCount,
      failed_count: partnerCount - removedCount,
    });
  }

  return {
    trackSearch,
    trackFilters,
    trackPopulationFilterApplied,
    trackPopulationFilterCleared,
    trackSharingPopoverViewed,
    trackPartnerSettingsOpened,
    trackRequestsDrawerOpened,
    trackOwnerChanged,
    trackBulkTagsSaved,
    trackBulkRemoveConfirmed,
  };
}
